/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getFlights, createFlight, updateFlight, cancelFlight, lookupAircraft, lookupAirport, getFlightDates, getServices } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plane, Plus, Pencil, Trash2, ArrowRight, Loader2, Fuel, ParkingCircle, MapPin, AlertTriangle, Search, Copy, Calendar as CalendarIcon, RotateCcw, PlaneLanding, PlaneTakeoff, ArrowUpDown, Save, ArrowUp, ArrowDown, SlidersHorizontal, Scale, Lock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionContext";
import { useAvailableYears } from "@/hooks/useAvailableYears";
import { FlightHistoryButton } from "@/components/FlightHistoryButton";
import { MvtButton } from "@/components/MvtButton";
import { BriefingButton } from "@/components/BriefingButton";
import { FlightDocButton } from "@/components/FlightDocButton";
import { SendPlanningButton } from "@/components/SendPlanningButton";
import { PprBadge } from "@/components/PprBadge";
import { DerogationBadge } from "@/components/DerogationBadge";

// Icon mapping for dynamic services
const serviceIconMap = {
  "fuel": "⛽",
  "car": "🚕",
  "utensils": "🍽️",
  "droplet": "🚿",
  "glass-water": "💧",
  "plug": "⚡",
  "coffee": "☕",
  "snowflake": "🧊",
  "plate-wheat": "🍽️",
  "shirt": "🧺",
  "wind": "🔧",
  "shield-alert": "⚠️",
  "accessibility": "♿",
  "thermometer-snowflake": "❄️",
  "circle": "•",
  "receipt": "🧾",
  "badge-check": "✅",
  "shield-check": "🛂",
  "warehouse": "🏠",
  "truck": "🚛",
};

// Color mapping for service badges - matches the old hardcoded colors
const serviceColorMap = {
  "fuel_request": "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300",
  "taxi_request": "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300",
  "catering_request": "bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300",
  "gpu_request": "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300",
  "wc_service_request": "bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300",
  "potable_water_request": "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
  "coffee_request": "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300",
  "ice_request": "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300",
  "dishes_request": "bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300",
  "laundry_request": "bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300",
  "asu_request": "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300",
  "de_icing_request": "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300",
  "detax_request": "bg-lime-100 dark:bg-lime-900/50 text-lime-700 dark:text-lime-300",
  "customs_request": "bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300",
  "border_control_request": "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300",
  "de_tax___customs_request": "bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300",
  "hangar_request": "bg-slate-100 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300",
  "towing_request": "bg-stone-100 dark:bg-stone-900/50 text-stone-700 dark:text-stone-300",
  "weapon_on_board": "bg-red-600 text-white font-bold animate-pulse",
  "wchr_request": "bg-blue-600 text-white font-bold animate-pulse",
  "pmr_request": "bg-blue-600 text-white font-bold animate-pulse",
};

// Get display name from field key
const getServiceDisplayName = (fieldKey) => {
  const nameMap = {
    "fuel_request": "FUEL",
    "taxi_request": "TAXI",
    "catering_request": "CATERING",
    "gpu_request": "GPU",
    "wc_service_request": "WC",
    "potable_water_request": "WATER",
    "coffee_request": "COFFEE",
    "ice_request": "ICE",
    "dishes_request": "DISHES",
    "laundry_request": "LAUNDRY",
    "asu_request": "ASU",
    "de_icing_request": "DE-ICING",
    "detax_request": "DETAX",
    "customs_request": "CUSTOMS",
    "de_tax___customs_request": "DETAX/CUSTOMS",
    "hangar_request": "HANGAR",
    "towing_request": "TOWING",
    "weapon_on_board": "⚠️ WEAPON",
    "wchr_request": "♿ WCHR",
    "pmr_request": "♿ PMR",
  };
  return nameMap[fieldKey] || fieldKey.replace(/_request$/, "").replace(/_/g, " ").toUpperCase();
};

// Auto-format time input: inserts ':' after 2 digits (e.g., "1157" → "11:57")
const formatTimeInput = (value) => {
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length <= 2) return digits;
  return digits.slice(0, 2) + ':' + digits.slice(2, 4);
};

const initialFormData = {
  flight_date: "",
  flight_number: "",
  registration: "",
  time: "",
  pilot_in_command: "",
  crew_number: 1,
  origin: "",
  origin_name: "",
  origin_country: "",
  origin_schengen: true,
  destination: "",
  destination_name: "",
  destination_country: "",
  destination_schengen: true,
  flight_type: "",
  aircraft_type: "",
  mtow: 0,
  cargo_info: "",
  cargo_kg: 0,
  pax_in: 0,
  pax_out: 0,
  aoc: "",
  operator: "",
  notes: "",
  parking_position: ""
};

const flightTypes = [
  "Passenger",
  "Cargo",
  "Military",
  "State",
  "Diplomatic",
  "Medical",
  "Private",
  "Charter",
  "Positioning",
  "Training",
  "Government",
  "Technical",
  "Other"
];

const aircraftTypes = [
  "Citation XLS",
  "Citation CJ3",
  "Challenger 350",
  "Global 6000",
  "Falcon 2000",
  "Falcon 7X",
  "Gulfstream G550",
  "Gulfstream G650",
  "Learjet 45",
  "Phenom 300",
  "King Air 350",
  "Pilatus PC-12",
  "Other"
];

export default function FlightsPage() {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [allFlights, setAllFlights] = useState([]); // All flights for rotation lookup
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [cancelReason, setCancelReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "current_month");
  const [selectedYear, setSelectedYear] = useState("2026");
  const [under3TOnly, setUnder3TOnly] = useState(false);
  const [flightTypeFilter, setFlightTypeFilter] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [editingATD, setEditingATD] = useState(null);
  const [atdValue, setAtdValue] = useState("");
  const [savingATD, setSavingATD] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);

  // Dynamic list of years that actually have flight data (falls back to last 5 years)
  const years = useAvailableYears();

  // Calendar date picker state
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [flightDates, setFlightDates] = useState([]);
  
  // Dynamic services state
  const [services, setServices] = useState([]);
  
  // Sort state
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [manualSort, setManualSort] = useState(false); // true once user clicks the sort toggle
  
  // Rotation dialog state
  const [rotationDialogOpen, setRotationDialogOpen] = useState(false);
  const [rotationFlights, setRotationFlights] = useState({ arrival: null, localDeparture: null, localArrival: null, departure: null });
  const [rotationLoading, setRotationLoading] = useState(false);
  
  // Aircraft Change dialog state (for rotation - update both movements)
  const [aircraftChangeDialogOpen, setAircraftChangeDialogOpen] = useState(false);
  const [aircraftChangeData, setAircraftChangeData] = useState({
    registration: "",
    arrival_flight_number: "",
    arrival_origin: "",
    arrival_destination: "",
    arrival_date: "",
    arrival_time: "",
    arrival_pax_in: 0,
    arrival_pax_out: 0,
    departure_flight_number: "",
    departure_origin: "",
    departure_destination: "",
    departure_date: "",
    departure_time: "",
    departure_pax_in: 0,
    departure_pax_out: 0
  });

  // Permission checks
  const canCreate = hasPermission("flights", "create");
  const canEdit = hasPermission("flights", "edit");
  const canDelete = hasPermission("flights", "delete");

  const tabs = [
    { id: "all", label: "All Flights" },
    { id: "current_month", label: "Current Month" },
    { id: "past", label: "Past Flights" },
    { id: "today", label: "Today's Flights" },
    { id: "tomorrow", label: "Tomorrow's Flights" },
    { id: "upcoming", label: "Upcoming Flights" }
  ];

  // Filter flights based on search query (includes aircraft_type)
  const filteredFlights = flights.filter(flight => {
    // "Under 3T" filter: show all flights (arrivals & departures) with MTOW < 3000 kg
    if (under3TOnly) {
      const mtow = Number(flight.mtow) || 0;
      if (!(mtow > 0 && mtow < 3000)) return false;
    }
    // Flight type filter
    if (flightTypeFilter) {
      const ft = (flight.flight_type || "").trim();
      if (flightTypeFilter === "__missing__") {
        if (ft) return false;
      } else if (ft !== flightTypeFilter) {
        return false;
      }
    }
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      flight.registration?.toLowerCase().includes(query) ||
      flight.origin?.toLowerCase().includes(query) ||
      flight.destination?.toLowerCase().includes(query) ||
      flight.operator?.toLowerCase().includes(query) ||
      flight.flight_number?.toLowerCase().includes(query) ||
      flight.origin_name?.toLowerCase().includes(query) ||
      flight.destination_name?.toLowerCase().includes(query) ||
      flight.aircraft_type?.toLowerCase().includes(query)
    );
  });

  // Distinct flight types present in the current view (for the filter dropdown)
  const flightTypeOptions = [...new Set(flights.map(f => (f.flight_type || "").trim()).filter(Boolean))].sort();
  const hasMissingType = flights.some(f => !(f.flight_type || "").trim());

  // Apply sort direction.
  // Default for All / Current Month / Past tabs: most recent (nearest to now) flights first,
  // as one clean chronological list (newest date on top, oldest at the bottom — no interleaving).
  // The manual sort toggle overrides this.
  const useNearestSort = !manualSort && !selectedDate && ["all", "current_month", "past"].includes(activeTab);
  const sortedFlights = [...filteredFlights].sort((a, b) => {
    const dateA = `${a.flight_date}T${a.time || '00:00'}`;
    const dateB = `${b.flight_date}T${b.time || '00:00'}`;
    if (useNearestSort) {
      return dateB.localeCompare(dateA); // newest / most recent first
    }
    return sortDirection === "asc" ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
  });

  // Fetch services on mount
  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const response = await getServices();
        // Only active services
        setServices(response.data.filter(s => s.is_active));
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };
    fetchServicesData();
  }, []);

  // Handle edit query parameter from rotation dialog
  useEffect(() => {
    const editFlightId = searchParams.get("edit");
    if (editFlightId && flights.length > 0) {
      const flightToEdit = flights.find(f => f.id === editFlightId);
      if (flightToEdit) {
        handleOpenDialog(flightToEdit);
        // Clear the query parameter
        setSearchParams({});
      }
    }
  }, [searchParams, flights]);

  useEffect(() => {
    fetchFlights();
    setManualSort(false); // reset to "nearest first" default when switching tab/date
  }, [activeTab, selectedDate, selectedYear]);

  // Fetch flight dates for calendar highlighting
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await getFlightDates();
        // Convert date strings to Date objects
        const dates = response.data.dates.map(d => parseISO(d));
        setFlightDates(dates);
      } catch (error) {
        console.error("Failed to fetch flight dates:", error);
      }
    };
    fetchDates();
  }, []);

  const fetchFlights = async () => {
    setLoading(true);
    try {
      // If a specific date is selected, use "specific_date" status
      const params = selectedDate 
        ? { status: "specific_date", specific_date: format(selectedDate, "yyyy-MM-dd") }
        : { status: activeTab };
      // Year filter applies to the "All Flights" view
      if (!selectedDate && activeTab === "all") {
        params.year = parseInt(selectedYear);
      }
      
      const response = await getFlights(params);
      setFlights(response.data);
    } catch (error) {
      toast.error("Failed to fetch flights");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all flights for rotation lookup
  const fetchAllFlights = async () => {
    try {
      const response = await getFlights({});
      setAllFlights(response.data);
    } catch (error) {
      console.error("Failed to fetch all flights:", error);
    }
  };

  // Initial load of all flights for rotation
  useEffect(() => {
    fetchAllFlights();
  }, []);

  // Find rotation for a flight
  const findRotation = async (flight) => {
    setRotationLoading(true);
    setRotationFlights({ arrival: null, localDeparture: null, localArrival: null, departure: null });
    setRotationDialogOpen(true);
    const registration = flight.registration;

    // Always work from the freshest data so a just-created rotation is included.
    // Filter server-side by registration so we get ALL legs regardless of year
    // (the unfiltered endpoint is capped and can drop recent flights).
    let pool = allFlights;
    try {
      const response = await getFlights({ registration });
      pool = response.data;
    } catch (error) {
      console.error("Failed to refresh flights for rotation:", error);
    }

    // Find ALL flights with same registration (exclude canceled)
    const sameRegFlights = pool.filter(f =>
      f.registration === registration && f.status !== "canceled"
    );

    // Sort by date and time
    const sortedFlights = sameRegFlights.sort((a, b) => {
      const dateA = new Date(`${a.flight_date}T${a.time || '00:00'}`);
      const dateB = new Date(`${b.flight_date}T${b.time || '00:00'}`);
      return dateA - dateB;
    });

    // Anchor everything to the CLICKED flight's date/time so imported historic
    // legs (previous years) are never matched as the rotation partner.
    const clickedTime = new Date(`${flight.flight_date}T${flight.time || '00:00'}`);

    let arrival = null;
    let localDeparture = null;
    let localArrival = null;
    let departure = null;

    // Helper function to check flight type
    const isLocalFlight = (f) => f.origin?.toUpperCase() === "EBCI" && f.destination?.toUpperCase() === "EBCI";
    const isArrivalFlight = (f) => !isLocalFlight(f) && f.destination?.toUpperCase() === "EBCI";
    const isDepartureFlight = (f) => !isLocalFlight(f) && f.origin?.toUpperCase() === "EBCI";
    const timeOf = (f) => new Date(`${f.flight_date}T${f.time || '00:00'}`);

    // Nearest arrival (inbound) AT or BEFORE the clicked flight
    const arrivalsBefore = sortedFlights.filter(f => isArrivalFlight(f) && timeOf(f) <= clickedTime);
    arrival = arrivalsBefore.length ? arrivalsBefore[arrivalsBefore.length - 1] : null;

    // Find local flights after the arrival (or clicked flight)
    const searchStartTime = arrival ? timeOf(arrival) : clickedTime;
    const localFlights = sortedFlights
      .filter(f => isLocalFlight(f) && timeOf(f) >= searchStartTime)
      .sort((a, b) => timeOf(a) - timeOf(b));

    if (localFlights.length >= 1) localDeparture = localFlights[0];
    if (localFlights.length >= 2) localArrival = localFlights[1];

    // Nearest departure (outbound) AT or AFTER the local flights / clicked flight
    const searchAfterTime = localArrival
      ? timeOf(localArrival)
      : localDeparture
        ? timeOf(localDeparture)
        : clickedTime;
    const departuresAfter = sortedFlights.filter(f => isDepartureFlight(f) && timeOf(f) >= searchAfterTime);
    departure = departuresAfter.length ? departuresAfter[0] : null;

    setRotationFlights({ arrival, localDeparture, localArrival, departure });
    setRotationLoading(false);
  };

  // Open Aircraft Change dialog to update both movements in rotation
  const openAircraftChangeDialog = () => {
    const arrival = rotationFlights.arrival;
    const departure = rotationFlights.departure;
    
    setAircraftChangeData({
      registration: arrival?.registration || departure?.registration || "",
      arrival_flight_number: arrival?.flight_number || "",
      arrival_origin: arrival?.origin || "",
      arrival_destination: arrival?.destination || "EBCI",
      arrival_date: arrival?.flight_date || "",
      arrival_time: arrival?.time || "",
      arrival_pax_in: arrival?.pax_in || 0,
      arrival_pax_out: arrival?.pax_out || 0,
      departure_flight_number: departure?.flight_number || "",
      departure_origin: departure?.origin || "EBCI",
      departure_destination: departure?.destination || "",
      departure_date: departure?.flight_date || "",
      departure_time: departure?.time || "",
      departure_pax_in: departure?.pax_in || 0,
      departure_pax_out: departure?.pax_out || 0
    });
    setAircraftChangeDialogOpen(true);
  };
  
  // Save Aircraft Change - update both movements
  const saveAircraftChange = async () => {
    setSaving(true);
    try {
      const updates = [];
      
      if (rotationFlights.arrival) {
        const arrivalUpdate = {
          ...rotationFlights.arrival,
          flight_number: aircraftChangeData.arrival_flight_number,
          registration: aircraftChangeData.registration,
          origin: aircraftChangeData.arrival_origin,
          destination: aircraftChangeData.arrival_destination,
          flight_date: aircraftChangeData.arrival_date,
          time: aircraftChangeData.arrival_time,
          pax_in: parseInt(aircraftChangeData.arrival_pax_in) || 0,
          pax_out: parseInt(aircraftChangeData.arrival_pax_out) || 0
        };
        updates.push(updateFlight(rotationFlights.arrival.id, arrivalUpdate));
      }
      
      if (rotationFlights.departure) {
        const departureUpdate = {
          ...rotationFlights.departure,
          flight_number: aircraftChangeData.departure_flight_number,
          registration: aircraftChangeData.registration,
          origin: aircraftChangeData.departure_origin,
          destination: aircraftChangeData.departure_destination,
          flight_date: aircraftChangeData.departure_date,
          time: aircraftChangeData.departure_time,
          pax_in: parseInt(aircraftChangeData.departure_pax_in) || 0,
          pax_out: parseInt(aircraftChangeData.departure_pax_out) || 0
        };
        updates.push(updateFlight(rotationFlights.departure.id, departureUpdate));
      }
      
      if (rotationFlights.localDeparture) {
        const localDepUpdate = {
          ...rotationFlights.localDeparture,
          registration: aircraftChangeData.registration
        };
        updates.push(updateFlight(rotationFlights.localDeparture.id, localDepUpdate));
      }
      
      if (rotationFlights.localArrival) {
        const localArrUpdate = {
          ...rotationFlights.localArrival,
          registration: aircraftChangeData.registration
        };
        updates.push(updateFlight(rotationFlights.localArrival.id, localArrUpdate));
      }
      
      await Promise.all(updates);
      await fetchFlights();
      await fetchAllFlights();
      
      toast.success("Aircraft change applied to all movements");
      setAircraftChangeDialogOpen(false);
      setRotationDialogOpen(false);
    } catch (error) {
      console.error("Failed to apply aircraft change:", error);
      toast.error("Failed to apply aircraft change");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveATD = async (flightId, fieldName = 'atd') => {
    if (!atdValue || !/^\d{2}:\d{2}$/.test(atdValue)) {
      toast.error(`Please enter a valid ${fieldName.toUpperCase()} time (HH:MM)`);
      return;
    }
    setSavingATD(true);
    try {
      const response = await updateFlight(flightId, { [fieldName]: atdValue });
      if (response.data?.parking_warning) {
        toast.warning(response.data.parking_warning);
      }
      toast.success(`${fieldName.toUpperCase()} saved successfully`);
      setEditingATD(null);
      setAtdValue("");
      fetchFlights();
    } catch (error) {
      toast.error(`Failed to save ${fieldName.toUpperCase()}`);
    } finally {
      setSavingATD(false);
    }
  };

  const handleOpenDialog = (flight = null) => {
    if (flight) {
      setSelectedFlight(flight);
      setFormData({
        ...flight,
        parking_position: flight.parking_position || "",
        atd: flight.atd || "",
        ata: flight.ata || ""
      });
    } else {
      setSelectedFlight(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  // Duplicate flight - copies all data except clears ID and sets a new date
  // If arrival flight is duplicated, it creates a departure with swapped origin/destination
  const handleDuplicateFlight = (flight) => {
    // Determine if this is an arrival (destination is EBCI)
    const isArrival = flight.destination?.toUpperCase() === "EBCI";
    
    setSelectedFlight(null); // Important: null means creating new flight
    
    if (isArrival) {
      // For arrivals: create a departure (swap origin/destination, clear date/time)
      setFormData({
        ...flight,
        id: undefined,
        flight_date: "", // Empty date for user to fill
        time: "", // Empty time for user to fill
        flight_number: "", // Clear flight number for new entry
        atd: "",
        status: "active",
        // Swap origin and destination
        origin: flight.destination,
        origin_name: flight.destination_name,
        origin_country: flight.destination_country,
        origin_schengen: flight.destination_schengen,
        origin_non_eu: flight.destination_non_eu,
        destination: "", // Empty for user to fill
        destination_name: "",
        destination_country: "",
        destination_schengen: true,
        destination_non_eu: false,
        parking_position: flight.parking_position || "",
        notes: `Return flight for ${flight.flight_number || "arrival"}`
      });
      toast.info("Departure created from arrival - fill in destination, date & time");
    } else {
      // For departures/local: simple duplicate with tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split("T")[0];
      
      setFormData({
        ...flight,
        id: undefined,
        flight_date: tomorrowStr,
        status: "active",
        atd: "",
        parking_position: flight.parking_position || "",
        notes: flight.notes || ""
      });
      toast.info("Flight duplicated - adjust the date and save");
    }
    setDialogOpen(true);
  };


  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFlight(null);
    setFormData(initialFormData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-fill from aircraft database when registration changes
  const handleRegistrationChange = async (value) => {
    handleChange("registration", value);
    
    if (value.length >= 3) {
      try {
        const response = await lookupAircraft(value);
        if (response.data) {
          const aircraft = response.data;
          // Build list of auto-filled fields for the toast message
          const filledFields = [];
          
          const updates = { registration: value };
          
          if (aircraft.aircraft_type) {
            updates.aircraft_type = aircraft.aircraft_type;
            filledFields.push("Aircraft Type");
          }
          if (aircraft.operator) {
            updates.operator = aircraft.operator;
            filledFields.push("Operator");
          }
          if (aircraft.mtow && aircraft.mtow > 0) {
            updates.mtow = aircraft.mtow;
            filledFields.push("MTOW");
          }
          if (aircraft.aoc) {
            updates.aoc = aircraft.aoc;
            filledFields.push("AOC");
          }
          
          if (filledFields.length > 0) {
            setFormData(prev => ({
              ...prev,
              ...updates
            }));
            toast.success(`Auto-filled: ${filledFields.join(", ")}`);
          }
        }
      } catch (e) {
        // No match found, ignore
      }
    }
  };

  // Auto-fill from airport database when origin ICAO changes
  const handleOriginChange = async (value) => {
    handleChange("origin", value.toUpperCase());
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setFormData(prev => {
            const isArrival = (prev.destination || "").toUpperCase() === "EBCI";
            const extraSchengen = airport.schengen === false;
            const nonEu = airport.non_eu === true || extraSchengen;
            return {
              ...prev,
              origin: value.toUpperCase(),
              origin_name: airport.airport_name || "",
              origin_country: airport.country || "",
              origin_schengen: airport.schengen !== false,
              origin_non_eu: nonEu,
              // For arrivals to EBCI: auto-tick Border Control (extra-Schengen) and Customs (non-EU)
              ...(isArrival && extraSchengen ? { border_control_request: true } : {}),
              ...(isArrival && nonEu ? { customs_request: true } : {}),
            };
          });
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Origin: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        // Reset airport info if not found
        setFormData(prev => ({
          ...prev,
          origin: value.toUpperCase(),
          origin_name: "",
          origin_country: "",
          origin_schengen: true
        }));
      }
    }
  };

  // Auto-fill from airport database when destination ICAO changes
  const handleDestinationChange = async (value) => {
    handleChange("destination", value.toUpperCase());
    
    if (value.length >= 3) {
      try {
        const response = await lookupAirport(value);
        if (response.data) {
          const airport = response.data;
          setFormData(prev => {
            const isDeparture = (prev.origin || "").toUpperCase() === "EBCI";
            const extraSchengen = airport.schengen === false;
            const nonEu = airport.non_eu === true || extraSchengen;
            return {
              ...prev,
              destination: value.toUpperCase(),
              destination_name: airport.airport_name || "",
              destination_country: airport.country || "",
              destination_schengen: airport.schengen !== false,
              destination_non_eu: nonEu,
              // For departures from EBCI: auto-tick Border Control (extra-Schengen) and Customs (non-EU)
              ...(isDeparture && extraSchengen ? { border_control_request: true } : {}),
              ...(isDeparture && nonEu ? { customs_request: true } : {}),
            };
          });
          const schengenStatus = airport.schengen ? "✓ Schengen" : "⚠ Non-Schengen";
          toast.success(`Destination: ${airport.airport_name || airport.icao_code} (${schengenStatus})`);
        }
      } catch (e) {
        // Reset airport info if not found
        setFormData(prev => ({
          ...prev,
          destination: value.toUpperCase(),
          destination_name: "",
          destination_country: "",
          destination_schengen: true
        }));
      }
    }
  };

  const handleSubmit = async () => {
    // Check which fields are missing/invalid
    const missing = [];
    if (!formData.flight_date) {
      missing.push("Date");
    } else {
      // Validate date is a real date (e.g., reject April 31)
      const d = new Date(formData.flight_date + "T00:00:00");
      if (isNaN(d.getTime())) {
        toast.error("Invalid date - please check the date entered");
        return;
      }
    }
    if (!formData.flight_number) missing.push("Flight Number");
    if (!formData.origin) missing.push("Origin");
    if (!formData.destination) missing.push("Destination");
    
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      if (selectedFlight) {
        const response = await updateFlight(selectedFlight.id, formData);
        // Show parking sync/warning messages
        if (response.data?.parking_synced_to) {
          toast.success(`Parking synced to departure flight ${response.data.parking_synced_to}`);
        }
        if (response.data?.parking_warning) {
          toast.warning(response.data.parking_warning);
        }
        toast.success("Flight updated successfully");
      } else {
        await createFlight(formData);
        toast.success("Flight created successfully");
      }
      handleCloseDialog();
      fetchFlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save flight");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedFlight || !cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }
    
    try {
      await cancelFlight(selectedFlight.id, cancelReason);
      toast.success("Flight canceled successfully");
      setCancelDialogOpen(false);
      setSelectedFlight(null);
      setCancelReason("");
      fetchFlights();
    } catch (error) {
      toast.error("Failed to cancel flight");
    }
  };

  const openCancelDialog = (flight) => {
    setSelectedFlight(flight);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const inputClass = "bg-white border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8]";
  const labelClass = "text-[#0f172a] font-medium";

  return (
    <div className="animate-fade-in" data-testid="flights-page">
      {/* Combined Sticky Header - Title, Button, Tabs, and Search all together */}
      <div className="sticky-header-combined">
        {/* Title Row - Compact */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit'] truncate">Flights Management</h1>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* Mobile: results count + Filters toggle */}
            <span className="md:hidden text-xs text-[#64748b] dark:text-gray-400 whitespace-nowrap">
              {sortedFlights.length} found
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileFiltersOpen(v => !v)}
              className="md:hidden gap-1 border-[#d4af37] text-[#d4af37]"
              data-testid="mobile-filters-toggle"
            >
              <SlidersHorizontal size={14} /> Filters
            </Button>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="hidden md:flex w-28 h-9" data-testid="flights-year-selector">
                <span className="text-xs text-[#64748b] mr-1">Year</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="hidden md:inline text-sm text-[#64748b] dark:text-gray-400">
              {sortedFlights.length} flight{sortedFlights.length !== 1 ? "s" : ""} found
            </span>
            {canCreate && (
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-semibold"
                data-testid="add-flight-btn"
              >
                <Plus size={20} className="md:mr-2" />
                <span className="hidden md:inline">Add Flight</span>
              </Button>
            )}
          </div>
        </div>

        {/* Tabs + Search - Compact Row (collapsible on mobile) */}
        <div className={`${mobileFiltersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:items-center md:justify-between gap-3 flex-wrap`}>
          {/* Mobile-only Year selector inside the filters panel */}
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="md:hidden w-full h-9" data-testid="flights-year-selector-mobile">
              <span className="text-xs text-[#64748b] mr-1">Year</span>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Tabs */}
          <div className="flex gap-1 flex-wrap items-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedDate(null);
                }}
                className={`tab-button ${activeTab === tab.id && !selectedDate ? "active" : ""}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
            
            {/* Calendar Date Picker */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`ml-1 gap-1 ${selectedDate ? "bg-[#d4af37] text-[#0f172a] border-[#d4af37] hover:bg-[#b8972f]" : "border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0f172a] dark:text-[#d4af37] dark:border-[#d4af37]"}`}
                  data-testid="calendar-picker-btn"
                >
                  <CalendarIcon size={14} />
                  {selectedDate ? format(selectedDate, "dd MMM") : "Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white dark:bg-[#1e293b]" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setCalendarOpen(false);
                  }}
                  modifiers={{
                    hasFlights: flightDates
                  }}
                  modifiersClassNames={{
                    hasFlights: "day-has-flights"
                  }}
                  initialFocus
                />
                {selectedDate && (
                  <div className="p-2 border-t dark:border-[#334155]">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedDate(null);
                        setCalendarOpen(false);
                      }}
                      className="w-full text-[#64748b]"
                    >
                      Clear Date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Under 3T (PPR) filter toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUnder3TOnly(v => !v)}
              className={`ml-1 gap-1 ${under3TOnly ? "bg-red-600 text-white border-red-600 hover:bg-red-700 hover:text-white" : "border-red-500 text-red-600 hover:bg-red-600 hover:text-white dark:text-red-400 dark:border-red-500"}`}
              data-testid="filter-under-3t-btn"
              title="Show only arrivals with MTOW under 3000 kg (PPR needed)"
            >
              Under 3T
            </Button>

            {/* Flight type filter */}
            <Select value={flightTypeFilter} onValueChange={setFlightTypeFilter}>
              <SelectTrigger className="ml-1 w-40 h-9 bg-white dark:bg-[#1e293b]" data-testid="filter-flight-type">
                <SelectValue placeholder="Flight Type" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#1e293b]">
                {flightTypeOptions.map((ft) => (
                  <SelectItem key={ft} value={ft}>{ft}</SelectItem>
                ))}
                {hasMissingType && (
                  <SelectItem value="__missing__">Missing Type</SelectItem>
                )}
              </SelectContent>
            </Select>
            {flightTypeFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFlightTypeFilter("")}
                className="h-9 px-2 text-[#64748b] hover:text-[#0f172a] dark:hover:text-white"
                data-testid="clear-flight-type-btn"
                title="Clear flight type filter"
              >
                Clear
              </Button>
            )}
          </div>
          
          {/* Search + Sort */}
          <div className="flex items-center gap-2 w-full md:flex-1 md:max-w-md">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 bg-white dark:bg-[#1e293b]"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setManualSort(true); setSortDirection(prev => prev === "asc" ? "desc" : "asc"); }}
              className="h-9 px-3 gap-1 border-[#e2e8f0] text-[#64748b] hover:text-[#d4af37] hover:border-[#d4af37]"
              title={`Sort ${sortDirection === "asc" ? "Newest First" : "Oldest First"}`}
              data-testid="sort-toggle-btn"
            >
              {sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {sortDirection === "asc" ? "Oldest" : "Newest"}
            </Button>
            {activeTab === "tomorrow" && !selectedDate && <SendPlanningButton />}
          </div>
        </div>
      </div>

      {/* Flight Cards */}
      <div className="page-content-with-sticky">
      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
        </div>
      ) : sortedFlights.length === 0 ? (
        <div className="empty-state">
          <Plane className="mx-auto" />
          <h3>No Flights Found</h3>
          <p>No flights found for this period or search criteria.</p>
          {canCreate && (
            <Button
              onClick={() => handleOpenDialog()}
              className="mt-4 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
            >
              <Plus size={20} className="mr-2" />
              Add Flight
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sortedFlights.map((flight, index) => {
            const isLocal = flight.origin?.toUpperCase() === "EBCI" && flight.destination?.toUpperCase() === "EBCI";
            const isDeparture = !isLocal && flight.origin?.toUpperCase() === "EBCI";
            const isArrival = !isLocal && flight.destination?.toUpperCase() === "EBCI";
            
            // For local flights, determine if it's the departure or arrival movement
            let isLocalDeparture = false;
            let isLocalArrival = false;
            if (isLocal) {
              // Find other local flights with same registration on same date
              const sameRegLocalFlights = flights.filter(f => 
                f.registration === flight.registration &&
                f.flight_date === flight.flight_date &&
                f.origin?.toUpperCase() === "EBCI" && 
                f.destination?.toUpperCase() === "EBCI"
              ).sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));
              
              if (sameRegLocalFlights.length >= 2) {
                const flightIndex = sameRegLocalFlights.findIndex(f => f.id === flight.id);
                isLocalDeparture = flightIndex === 0; // First one is departure
                isLocalArrival = flightIndex === 1;   // Second one is arrival
              } else {
                isLocalDeparture = true; // Default to departure if only one local flight
              }
            }
            
            const scheduledLabel = isLocal ? (isLocalArrival ? "STA" : "STD") : (isDeparture ? "STD" : "STA");
            const actualLabel = isLocal ? (isLocalArrival ? "ATA" : "ATD") : (isDeparture ? "ATD" : "ATA");
            const timeColor = isLocal ? "text-orange-600" : (isDeparture ? "text-blue-600" : "text-green-600");
            
            // Use ata for arrivals, atd for departures
            const isArrivalMovement = (!isLocal && !isDeparture && isArrival) || (isLocal && isLocalArrival);
            const actualTimeField = isArrivalMovement ? 'ata' : 'atd';
            const actualTimeValue = isArrivalMovement ? (flight.ata || "") : (flight.atd || "");
            const actualColor = actualTimeValue ? timeColor : "text-[#94a3b8]";
            const actualIsManual = isArrivalMovement ? !!flight.ata_manual : !!flight.atd_manual;
            
            return (
              <Card
                key={flight.id}
                className="flight-row border-[#e2e8f0]"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`flight-card-${flight.id}`}
              >
                <CardContent className="p-4">
                  {/* Flight Type Badge */}
                  <div className="flex items-center gap-2 mb-2 md:mb-3 flex-wrap">
                    {isLocal && (
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                        ✈ LOCAL {isLocalArrival ? "ARRIVAL" : "DEPARTURE"}
                      </span>
                    )}
                    {isDeparture && (
                      <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                        ✈ DEPARTURE
                      </span>
                    )}
                    {isArrival && (
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                        ✈ ARRIVAL
                      </span>
                    )}
                    <PprBadge flight={flight} />
                    <DerogationBadge flight={flight} />
                    {!isLocal && !isDeparture && !isArrival && (
                      <span className="px-3 py-1 bg-gray-400 text-white text-xs font-bold rounded-full">
                        TRANSIT
                      </span>
                    )}
                  </div>
                  
                  {/* 4-Column Grid Layout - Full Width (stacks on mobile) */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr] gap-2 md:gap-4 md:items-center">
                    
                    {/* COLUMN 1: Flight Info + Operator */}
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isLocal ? "bg-orange-500" : isDeparture ? "bg-blue-500" : isArrival ? "bg-green-500" : "bg-[#0f172a]"
                      }`}>
                        <Plane size={20} className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono font-bold text-[#0f172a] dark:text-white text-lg">{flight.flight_number}</p>
                          {(flight.flight_type || "").trim() ? (
                            <span className="flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 px-2 py-0.5 rounded-full" data-testid="flight-type-badge">
                              {flight.flight_type}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full" data-testid="flight-type-missing-badge">
                              <AlertTriangle size={12} />
                              Missing Type
                            </span>
                          )}
                          {(flight.flight_type || "").trim().toLowerCase() === "cargo" && (
                            <span className="flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full" data-testid="cargo-kg-badge">
                              <Scale size={12} />
                              {flight.cargo_kg || 0} kg
                            </span>
                          )}
                          {flight.fuel_request && (
                            <span className="flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">
                              <Fuel size={12} />
                              Fuel
                            </span>
                          )}
                          {flight.parking_position && (
                            <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                              <ParkingCircle size={12} />
                              {flight.parking_position}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#64748b] dark:text-gray-400 truncate">
                          {flight.aircraft_type || "N/A"} • {flight.registration || "N/A"}
                        </p>
                        <p className="text-xs text-[#0f172a] dark:text-gray-200 font-medium truncate">
                          {flight.operator || "N/A"} {flight.aoc && <span className="text-[#d4af37]">• AOC: {flight.aoc}</span>}
                        </p>
                      </div>
                    </div>

                    {/* COLUMN 2: Route */}
                    <div className="flex items-center justify-center gap-3">
                      {/* Origin */}
                      <div className="text-right relative pt-6" style={{ width: '110px' }}>
                        {/* Origin Non-Schengen / Non-EU alternating badge */}
                        {(flight.origin_schengen === false && flight.origin_non_eu) ? (
                          <span className="absolute" style={{ top: '-2px', right: '0' }}>
                            <span className="badge-non-schengen animate-badge-switch-1">NON-SCHENGEN</span>
                            <span className="badge-non-eu absolute inset-0 animate-badge-switch-2">NON-EU</span>
                          </span>
                        ) : flight.origin_schengen === false ? (
                          <span className="badge-non-schengen absolute" style={{ top: '-2px', right: '0' }}>NON-SCHENGEN</span>
                        ) : flight.origin_non_eu ? (
                          <span className="badge-non-eu absolute" style={{ top: '-2px', right: '0' }}>NON-EU</span>
                        ) : null}
                        <p className="font-bold text-[#0f172a] dark:text-white text-lg">{flight.origin}</p>
                        <p className="text-[10px] text-[#64748b] dark:text-gray-400 uppercase truncate">{flight.origin_name || ""}</p>
                      </div>

                      {/* Arrow */}
                      <div className="flex-shrink-0 pt-6">
                        <Plane size={18} className="text-[#d4af37] rotate-90" />
                      </div>

                      {/* Destination */}
                      <div className="text-left relative pt-6" style={{ width: '110px' }}>
                        {/* Destination Non-Schengen / Non-EU alternating badge */}
                        {(flight.destination_schengen === false && flight.destination_non_eu) ? (
                          <span className="absolute" style={{ top: '-2px', left: '0' }}>
                            <span className="badge-non-schengen animate-badge-switch-1">NON-SCHENGEN</span>
                            <span className="badge-non-eu absolute inset-0 animate-badge-switch-2">NON-EU</span>
                          </span>
                        ) : flight.destination_schengen === false ? (
                          <span className="badge-non-schengen absolute" style={{ top: '-2px', left: '0' }}>NON-SCHENGEN</span>
                        ) : flight.destination_non_eu ? (
                          <span className="badge-non-eu absolute" style={{ top: '-2px', left: '0' }}>NON-EU</span>
                        ) : null}
                        <p className="font-bold text-[#0f172a] dark:text-white text-lg">{flight.destination}</p>
                        <p className="text-[10px] text-[#64748b] dark:text-gray-400 uppercase truncate">{flight.destination_name || ""}</p>
                      </div>
                    </div>

                    {/* COLUMN 3: Date/Time/PAX */}
                    <div className="flex items-center justify-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-[#64748b] dark:text-gray-400 text-xs">Date</p>
                        <p className="font-semibold text-[#0f172a] dark:text-white">
                          {format(parseISO(flight.flight_date), "dd MMM yyyy")}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className={`text-xs font-bold ${timeColor}`}>{scheduledLabel}</p>
                        <p className={`font-mono font-semibold ${timeColor}`}>{flight.time}</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-xs font-bold ${actualColor} flex items-center justify-center gap-0.5`}>
                          {actualLabel}
                          {actualIsManual && actualTimeValue && (
                            <Lock size={9} className="text-amber-500" data-testid={`manual-time-lock-${flight.id}`} />
                          )}
                        </p>
                        {editingATD === flight.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="text"
                              value={atdValue}
                              onChange={(e) => setAtdValue(formatTimeInput(e.target.value))}
                              placeholder="HH:MM"
                              maxLength={5}
                              className={`w-20 h-7 text-xs px-1 bg-white font-mono text-center ${isDeparture ? "border-blue-500" : "border-green-500"}`}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveATD(flight.id, actualTimeField); }}
                              data-testid={`atd-input-${flight.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSaveATD(flight.id, actualTimeField)}
                              disabled={savingATD}
                              className={`h-7 px-2 text-white ${isDeparture ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}`}
                              data-testid={`save-atd-${flight.id}`}
                            >
                              {savingATD ? <Loader2 size={12} className="animate-spin" /> : "✓"}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 justify-center">
                            <p className={`font-mono font-semibold ${actualColor}`}>
                              {actualTimeValue || "--:--"}
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingATD(flight.id); setAtdValue(actualTimeValue || ""); }}
                              className="h-5 w-5 p-0 text-[#64748b]"
                              data-testid={`edit-atd-${flight.id}`}
                            >
                              <Pencil size={10} />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-[#64748b] text-xs">PAX</p>
                        <p className="font-semibold text-[#0f172a]">
                          <span className="text-green-600">↓{flight.pax_in || 0}</span>
                          <span className="mx-1">/</span>
                          <span className="text-blue-600">↑{flight.pax_out || 0}</span>
                        </p>
                      </div>
                    </div>

                    {/* COLUMN 4: Notes, Services & Actions */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {/* Services - Dynamic rendering from services state */}
                        <div className="flex flex-wrap gap-1 mb-1">
                          {services.map((service) => {
                            const fieldKey = service.field_key;
                            // Check if this service is enabled for this flight
                            if (!flight[fieldKey]) return null;
                            
                            const colorClass = serviceColorMap[fieldKey] || "bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300";
                            const displayName = getServiceDisplayName(fieldKey);
                            
                            return (
                              <span 
                                key={fieldKey}
                                className={`text-[10px] px-1.5 py-0.5 rounded ${colorClass}`}
                              >
                                {displayName}
                              </span>
                            );
                          })}
                          {/* Also check for any services not in the services list but present on flight */}
                          {Object.keys(flight).filter(key => 
                            key.endsWith('_request') && 
                            flight[key] === true && 
                            !services.find(s => s.field_key === key)
                          ).map(fieldKey => {
                            const colorClass = serviceColorMap[fieldKey] || "bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300";
                            const displayName = getServiceDisplayName(fieldKey);
                            
                            return (
                              <span 
                                key={fieldKey}
                                className={`text-[10px] px-1.5 py-0.5 rounded ${colorClass}`}
                              >
                                {displayName}
                              </span>
                            );
                          })}
                          {/* Special cases: weapon_on_board doesn't have _request suffix */}
                          {flight.weapon_on_board && !services.find(s => s.field_key === 'weapon_on_board') && (
                            <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded font-bold animate-pulse">⚠️ WEAPON</span>
                          )}
                        </div>
                        {/* Notes - Multiple lines, readable size */}
                        {flight.notes && (
                          <p className="text-sm text-[#374151] dark:text-gray-300 leading-snug" style={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }} title={flight.notes}>
                            {flight.notes}
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* View All Rotations Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => findRotation(flight)}
                          className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10 h-8 w-8"
                          title="Show Rotation"
                          data-testid={`view-rotation-${flight.id}`}
                        >
                          <RotateCcw size={16} />
                        </Button>
                        <FlightHistoryButton flight={flight} variant="icon" />
                        <MvtButton flight={flight} variant="icon" />
                        {(isDeparture || isLocalDeparture) && (
                          <BriefingButton flight={flight} variant="icon" />
                        )}
                        {(isDeparture || isLocalDeparture) && (
                          <>
                            <FlightDocButton flight={flight} docType="manifest" variant="icon" />
                            <FlightDocButton flight={flight} docType="gendec" variant="icon" />
                          </>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(flight)}
                            className="text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9] h-8 w-8"
                            title="Edit Flight"
                            data-testid={`edit-flight-${flight.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                        )}
                        {canCreate && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicateFlight(flight)}
                            className="text-[#64748b] hover:text-blue-600 hover:bg-blue-50 h-8 w-8"
                            title="Duplicate Flight"
                            data-testid={`duplicate-flight-${flight.id}`}
                          >
                            <Copy size={16} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openCancelDialog(flight)}
                            className="text-[#64748b] hover:text-red-600 hover:bg-red-50 h-8 w-8"
                            title="Cancel Flight"
                            data-testid={`cancel-flight-${flight.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      </div>

      {/* Add/Edit Flight Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] bg-white border-[#e2e8f0]">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a]">
              {selectedFlight ? "Edit Flight" : "Add New Flight"}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="form-grid gap-4">
              {/* Row 1 */}
              <div className="space-y-2">
                <Label htmlFor="flight_date" className={labelClass}>Flight Date *</Label>
                <Input
                  id="flight_date"
                  type="date"
                  value={formData.flight_date}
                  onChange={(e) => handleChange("flight_date", e.target.value)}
                  className={inputClass}
                  data-testid="flight-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="flight_number" className={labelClass}>Flight Number *</Label>
                <Input
                  id="flight_number"
                  value={formData.flight_number}
                  onChange={(e) => handleChange("flight_number", e.target.value.toUpperCase())}
                  placeholder="e.g., CSB001"
                  className={inputClass}
                  data-testid="flight-number-input"
                />
              </div>

              {/* Row 2 */}
              <div className="space-y-2">
                <Label htmlFor="registration" className={labelClass}>Registration (auto-fill)</Label>
                <Input
                  id="registration"
                  value={formData.registration}
                  onChange={(e) => handleRegistrationChange(e.target.value.toUpperCase())}
                  placeholder="e.g., OO-ABC"
                  className={inputClass}
                  data-testid="registration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time" className={labelClass}>Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  className={inputClass}
                  data-testid="time-input"
                />
              </div>

              {/* Row 3 - Origin/Destination with Airport Info */}
              <div className="space-y-2">
                <Label htmlFor="origin" className={labelClass}>Origin (FROM) *</Label>
                <Input
                  id="origin"
                  value={formData.origin}
                  onChange={(e) => handleOriginChange(e.target.value)}
                  placeholder="e.g., EBCI"
                  className={`${inputClass} uppercase`}
                  data-testid="origin-input"
                />
                {formData.origin_name && (
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin size={12} className="text-[#64748b]" />
                    <span className="text-xs text-[#64748b]">{formData.origin_name}</span>
                    {!formData.origin_schengen && (
                      <Badge className="non-schengen-badge text-xs py-0">
                        ⚠ NON-SCHENGEN
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination" className={labelClass}>Destination (TO) *</Label>
                <Input
                  id="destination"
                  value={formData.destination}
                  onChange={(e) => handleDestinationChange(e.target.value)}
                  placeholder="e.g., LFPG"
                  className={`${inputClass} uppercase`}
                  data-testid="destination-input"
                />
                {formData.destination_name && (
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin size={12} className="text-[#64748b]" />
                    <span className="text-xs text-[#64748b]">{formData.destination_name}</span>
                    {!formData.destination_schengen && (
                      <Badge className="non-schengen-badge text-xs py-0">
                        ⚠ NON-SCHENGEN
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* NEW: Parking Position */}
              <div className="space-y-2">
                <Label htmlFor="parking_position" className={labelClass}>Parking Position (Apron/Stand)</Label>
                <Input
                  id="parking_position"
                  value={formData.parking_position}
                  onChange={(e) => handleChange("parking_position", e.target.value.toUpperCase())}
                  placeholder="e.g., Stand 12, Apron B"
                  className={inputClass}
                  data-testid="parking-position-input"
                />
              </div>

              {/* Pilot */}
              <div className="space-y-2">
                <Label htmlFor="pilot_in_command" className={labelClass}>Pilot in Command</Label>
                <Input
                  id="pilot_in_command"
                  value={formData.pilot_in_command}
                  onChange={(e) => handleChange("pilot_in_command", e.target.value)}
                  placeholder="Captain name"
                  className={inputClass}
                  data-testid="pilot-input"
                />
              </div>

              {/* Row 5 */}
              <div className="space-y-2">
                <Label htmlFor="crew_number" className={labelClass}>Crew Number</Label>
                <Input
                  id="crew_number"
                  type="number"
                  min="1"
                  value={formData.crew_number}
                  onChange={(e) => handleChange("crew_number", parseInt(e.target.value) || 1)}
                  className={inputClass}
                  data-testid="crew-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="flight_type" className={labelClass}>Type of Flight</Label>
                <Select
                  value={formData.flight_type}
                  onValueChange={(value) => handleChange("flight_type", value)}
                >
                  <SelectTrigger className={inputClass} data-testid="flight-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {flightTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Row 6 */}
              <div className="space-y-2">
                <Label htmlFor="aircraft_type" className={labelClass}>Aircraft Type</Label>
                <Input
                  id="aircraft_type"
                  value={formData.aircraft_type}
                  onChange={(e) => handleChange("aircraft_type", e.target.value)}
                  placeholder="e.g., Citation XLS, Gulfstream IV"
                  className={inputClass}
                  list="aircraft-types-list"
                  data-testid="aircraft-type-input"
                />
                <datalist id="aircraft-types-list">
                  {aircraftTypes.map((type) => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mtow" className={labelClass}>MTOW (kg)</Label>
                <Input
                  id="mtow"
                  type="number"
                  min="0"
                  value={formData.mtow}
                  onChange={(e) => handleChange("mtow", parseFloat(e.target.value) || 0)}
                  className={inputClass}
                  data-testid="mtow-input"
                />
              </div>

              {/* Row 7 */}
              <div className="space-y-2">
                <Label htmlFor="operator" className={labelClass}>Operator</Label>
                <Input
                  id="operator"
                  value={formData.operator}
                  onChange={(e) => handleChange("operator", e.target.value)}
                  placeholder="Operator name"
                  className={inputClass}
                  data-testid="operator-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aoc" className={labelClass}>AOC</Label>
                <Input
                  id="aoc"
                  value={formData.aoc}
                  onChange={(e) => handleChange("aoc", e.target.value)}
                  placeholder="AOC reference"
                  className={inputClass}
                  data-testid="aoc-input"
                />
              </div>

              {/* Row 8 */}
              <div className="space-y-2">
                <Label htmlFor="pax_in" className={labelClass}>PAX In</Label>
                <Input
                  id="pax_in"
                  type="number"
                  min="0"
                  value={formData.pax_in}
                  onChange={(e) => handleChange("pax_in", parseInt(e.target.value) || 0)}
                  className={inputClass}
                  data-testid="pax-in-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pax_out" className={labelClass}>PAX Out</Label>
                <Input
                  id="pax_out"
                  type="number"
                  min="0"
                  value={formData.pax_out}
                  onChange={(e) => handleChange("pax_out", parseInt(e.target.value) || 0)}
                  className={inputClass}
                  data-testid="pax-out-input"
                />
              </div>

              {/* Row 9 */}
              <div className="space-y-2">
                <Label htmlFor="cargo_info" className={labelClass}>Cargo Info</Label>
                <Input
                  id="cargo_info"
                  value={formData.cargo_info}
                  onChange={(e) => handleChange("cargo_info", e.target.value)}
                  placeholder="Cargo description"
                  className={inputClass}
                  data-testid="cargo-info-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cargo_kg" className={labelClass}>Cargo (KG)</Label>
                <Input
                  id="cargo_kg"
                  type="number"
                  min="0"
                  value={formData.cargo_kg}
                  onChange={(e) => handleChange("cargo_kg", parseFloat(e.target.value) || 0)}
                  className={inputClass}
                  data-testid="cargo-kg-input"
                />
              </div>

              {/* Row 10 - Services (Dynamic) */}
              <div className="col-span-2 space-y-3">
                <Label className={labelClass}>Services Requested</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {services.map((service) => {
                    const isWeapon = service.field_key === "weapon_on_board";
                    const isWchr = service.field_key === "wchr_request";
                    const icon = serviceIconMap[service.icon] || "•";
                    
                    return (
                      <div key={service.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={service.field_key}
                          checked={formData[service.field_key] || false}
                          onCheckedChange={(checked) => handleChange(service.field_key, checked)}
                          data-testid={`${service.field_key}-checkbox`}
                        />
                        <Label 
                          htmlFor={service.field_key} 
                          className={`cursor-pointer flex items-center gap-2 text-sm ${
                            isWeapon ? "text-red-600 font-bold" : 
                            isWchr ? "text-blue-600 font-bold" : 
                            "text-[#0f172a] dark:text-white"
                          }`}
                        >
                          {icon} {service.name}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Row 11 - Full width */}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="notes" className={labelClass}>Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                  className={inputClass}
                  data-testid="notes-input"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={handleCloseDialog} className="border-[#e2e8f0] text-[#64748b]">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-flight-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                selectedFlight ? "Update Flight" : "Create Flight"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Flight Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a]">Cancel Flight</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel flight {selectedFlight?.flight_number}? 
              Please provide a reason for the cancellation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel_reason" className="text-[#0f172a] font-medium">Reason for Cancellation *</Label>
            <Textarea
              id="cancel_reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter the reason for cancellation..."
              rows={3}
              className="mt-2 bg-white border-[#e2e8f0] text-[#0f172a]"
              data-testid="cancel-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              Cancel Flight
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rotation Dialog - Same as GlobalPlanningPage */}
      <Dialog open={rotationDialogOpen} onOpenChange={setRotationDialogOpen}>
        <DialogContent className="max-w-4xl bg-white dark:bg-[#1e293b] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] dark:text-white flex items-center gap-2">
              <RotateCcw size={24} className="text-[#d4af37]" />
              Flight Rotation
              {(rotationFlights.arrival?.registration || rotationFlights.departure?.registration || rotationFlights.localDeparture?.registration) && (
                <Badge className="ml-2 bg-[#0f172a] text-white text-sm">
                  {rotationFlights.arrival?.registration || rotationFlights.localDeparture?.registration || rotationFlights.departure?.registration}
                </Badge>
              )}
            </DialogTitle>
            {/* Aircraft Change Button - Update both movements */}
            {(rotationFlights.arrival || rotationFlights.departure) && (
              <Button
                onClick={openAircraftChangeDialog}
                className="mt-2 bg-[#d4af37] hover:bg-[#b8972f] text-white font-semibold"
                data-testid="aircraft-change-btn"
              >
                <ArrowUpDown size={16} className="mr-2" />
                Aircraft Change
              </Button>
            )}
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {/* 1. INBOUND - Arrival Flight */}
            <div className={`border-2 rounded-lg p-4 ${rotationFlights.arrival ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-white text-xs font-bold rounded-full ${rotationFlights.arrival ? 'bg-green-500' : 'bg-gray-400'}`}>
                    1. INBOUND
                  </span>
                  <span className={`font-semibold ${rotationFlights.arrival ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>Arrival to EBCI</span>
                </div>
                {rotationFlights.arrival && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setRotationDialogOpen(false);
                      handleOpenDialog(rotationFlights.arrival);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {rotationFlights.arrival ? (
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                    <a 
                      href={`https://planefinder.net/flight/number/${rotationFlights.arrival.flight_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-lg hover:underline text-green-600 dark:text-green-400"
                    >
                      {rotationFlights.arrival.flight_number || "-"}
                    </a>
                    <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.arrival.aircraft_type}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                    <p className="font-bold text-lg text-green-600 dark:text-green-400">
                      {rotationFlights.arrival.origin} → {rotationFlights.arrival.destination}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                    <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.arrival.flight_date), "dd MMM yyyy")}</p>
                    <p className="font-mono text-green-600 dark:text-green-400 font-bold">STA: {rotationFlights.arrival.time}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                    <p className="font-semibold dark:text-white">↓{rotationFlights.arrival.pax_in || 0} / ↑{rotationFlights.arrival.pax_out || 0}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-500 dark:text-gray-400 italic">No inbound flight found</p>
                  {canCreate && (
                    <Button
                      size="sm"
                      onClick={() => {
                        const reg = rotationFlights.departure?.registration || rotationFlights.localDeparture?.registration || "";
                        setRotationDialogOpen(false);
                        setSelectedFlight(null);
                        setFormData({
                          ...initialFormData,
                          registration: reg,
                          operator: rotationFlights.departure?.operator || rotationFlights.localDeparture?.operator || "",
                          aircraft_type: rotationFlights.departure?.aircraft_type || rotationFlights.localDeparture?.aircraft_type || "",
                          mtow: rotationFlights.departure?.mtow || rotationFlights.localDeparture?.mtow || 0,
                          destination: "EBCI",
                          destination_name: "Brussels South Charleroi",
                        });
                        setDialogOpen(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="add-inbound-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      <PlaneLanding size={14} className="mr-1" />
                      Add Inbound
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Connection Arrow */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center text-[#d4af37]">
                <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                <Plane size={20} className="rotate-180" />
                <div className="w-0.5 h-4 bg-[#d4af37]"></div>
              </div>
            </div>

            {/* 2. LOCAL DEPARTURE - Only show if exists */}
            {rotationFlights.localDeparture && (
              <>
                <div className="border-2 rounded-lg p-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-white text-xs font-bold rounded-full bg-orange-500">
                        2. LOCAL DEPARTURE
                      </span>
                      <span className="font-semibold text-orange-700 dark:text-orange-400">Local Flight Takeoff</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setRotationDialogOpen(false);
                        handleOpenDialog(rotationFlights.localDeparture);
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div>
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                      <a 
                        href={`https://planefinder.net/flight/number/${rotationFlights.localDeparture.flight_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono font-bold text-lg hover:underline text-orange-600 dark:text-orange-400"
                      >
                        {rotationFlights.localDeparture.flight_number || "-"}
                      </a>
                      <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.localDeparture.aircraft_type}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                      <p className="font-bold text-lg text-orange-600 dark:text-orange-400">
                        {rotationFlights.localDeparture.origin} ⟲ {rotationFlights.localDeparture.destination}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                      <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.localDeparture.flight_date), "dd MMM yyyy")}</p>
                      <p className="font-mono text-orange-600 dark:text-orange-400 font-bold">STD: {rotationFlights.localDeparture.time}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                      <p className="font-semibold dark:text-white">↓{rotationFlights.localDeparture.pax_in || 0} / ↑{rotationFlights.localDeparture.pax_out || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Connection Arrow */}
                <div className="flex justify-center">
                  <div className="flex flex-col items-center text-[#d4af37]">
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                    <Plane size={20} className="rotate-180" />
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                  </div>
                </div>
              </>
            )}

            {/* 3. LOCAL ARRIVAL - Only show if exists */}
            {rotationFlights.localArrival && (
              <>
                <div className="border-2 rounded-lg p-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-white text-xs font-bold rounded-full bg-orange-500">
                        3. LOCAL ARRIVAL
                      </span>
                      <span className="font-semibold text-orange-700 dark:text-orange-400">Local Flight Landing</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setRotationDialogOpen(false);
                        handleOpenDialog(rotationFlights.localArrival);
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div>
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                      <a 
                        href={`https://planefinder.net/flight/number/${rotationFlights.localArrival.flight_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono font-bold text-lg hover:underline text-orange-600 dark:text-orange-400"
                      >
                        {rotationFlights.localArrival.flight_number || "-"}
                      </a>
                      <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.localArrival.aircraft_type}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                      <p className="font-bold text-lg text-orange-600 dark:text-orange-400">
                        {rotationFlights.localArrival.origin} ⟲ {rotationFlights.localArrival.destination}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                      <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.localArrival.flight_date), "dd MMM yyyy")}</p>
                      <p className="font-mono text-orange-600 dark:text-orange-400 font-bold">STA: {rotationFlights.localArrival.time}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                      <p className="font-semibold dark:text-white">↓{rotationFlights.localArrival.pax_in || 0} / ↑{rotationFlights.localArrival.pax_out || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Connection Arrow */}
                <div className="flex justify-center">
                  <div className="flex flex-col items-center text-[#d4af37]">
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                    <Plane size={20} className="rotate-180" />
                    <div className="w-0.5 h-4 bg-[#d4af37]"></div>
                  </div>
                </div>
              </>
            )}

            {/* 4. OUTBOUND - Departure Flight */}
            <div className={`border-2 rounded-lg p-4 ${rotationFlights.departure ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 bg-gray-50 dark:bg-gray-800/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-white text-xs font-bold rounded-full ${rotationFlights.departure ? 'bg-blue-500' : 'bg-gray-400'}`}>
                    4. OUTBOUND
                  </span>
                  <span className={`font-semibold ${rotationFlights.departure ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>Departure from EBCI</span>
                </div>
                {rotationFlights.departure && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setRotationDialogOpen(false);
                      handleOpenDialog(rotationFlights.departure);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {rotationFlights.departure ? (
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Flight</p>
                    <a 
                      href={`https://planefinder.net/flight/number/${rotationFlights.departure.flight_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-lg hover:underline text-blue-600 dark:text-blue-400"
                    >
                      {rotationFlights.departure.flight_number || "-"}
                    </a>
                    <p className="text-sm text-[#64748b] dark:text-gray-400">{rotationFlights.departure.aircraft_type}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Route</p>
                    <p className="font-bold text-lg text-blue-600 dark:text-blue-400">
                      {rotationFlights.departure.origin} → {rotationFlights.departure.destination}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">Date & Time</p>
                    <p className="font-semibold dark:text-white">{format(parseISO(rotationFlights.departure.flight_date), "dd MMM yyyy")}</p>
                    <p className="font-mono text-blue-600 dark:text-blue-400 font-bold">STD: {rotationFlights.departure.time}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#64748b] dark:text-gray-400">PAX</p>
                    <p className="font-semibold dark:text-white">↓{rotationFlights.departure.pax_in || 0} / ↑{rotationFlights.departure.pax_out || 0}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-500 dark:text-gray-400 italic">No outbound flight found</p>
                  {canCreate && (
                    <Button
                      size="sm"
                      onClick={() => {
                        const reg = rotationFlights.arrival?.registration || rotationFlights.localArrival?.registration || "";
                        setRotationDialogOpen(false);
                        setSelectedFlight(null);
                        setFormData({
                          ...initialFormData,
                          registration: reg,
                          operator: rotationFlights.arrival?.operator || rotationFlights.localArrival?.operator || "",
                          aircraft_type: rotationFlights.arrival?.aircraft_type || rotationFlights.localArrival?.aircraft_type || "",
                          mtow: rotationFlights.arrival?.mtow || rotationFlights.localArrival?.mtow || 0,
                          origin: "EBCI",
                          origin_name: "Brussels South Charleroi",
                        });
                        setDialogOpen(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="add-outbound-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      <PlaneTakeoff size={14} className="mr-1" />
                      Add Outbound
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRotationDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aircraft Change Dialog */}
      <Dialog open={aircraftChangeDialogOpen} onOpenChange={setAircraftChangeDialogOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0f172a] dark:text-white">
              <ArrowUpDown size={20} className="text-[#d4af37]" />
              Aircraft Change
            </DialogTitle>
            <p className="text-sm text-[#64748b] dark:text-gray-400">Update aircraft details for all flights in this rotation</p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* New Registration */}
            <div>
              <Label className="text-[#0f172a] dark:text-white font-semibold">Registration</Label>
              <Input
                value={aircraftChangeData.registration}
                onChange={(e) => setAircraftChangeData({...aircraftChangeData, registration: e.target.value.toUpperCase()})}
                placeholder="e.g., OO-ABC"
                className="mt-1 font-mono text-lg bg-white dark:bg-[#0f172a]"
                data-testid="aircraft-change-registration"
              />
            </div>
            
            {/* Arrival (Inbound) Details */}
            {rotationFlights.arrival && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-300 dark:border-green-700">
                <h3 className="font-semibold text-green-700 dark:text-green-400 mb-3 flex items-center gap-2">
                  <PlaneLanding size={16} />
                  Inbound Flight
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Flight Number</Label>
                    <Input
                      value={aircraftChangeData.arrival_flight_number}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_flight_number: e.target.value.toUpperCase()})}
                      placeholder="e.g., FR1234"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Origin</Label>
                    <Input
                      value={aircraftChangeData.arrival_origin}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_origin: e.target.value.toUpperCase()})}
                      placeholder="e.g., EGLL"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Destination</Label>
                    <Input
                      value={aircraftChangeData.arrival_destination}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_destination: e.target.value.toUpperCase()})}
                      placeholder="EBCI"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Date</Label>
                    <Input
                      type="date"
                      value={aircraftChangeData.arrival_date}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_date: e.target.value})}
                      className="bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Time (STA)</Label>
                    <Input
                      type="time"
                      value={aircraftChangeData.arrival_time}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_time: e.target.value})}
                      className="bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#64748b] dark:text-gray-400 text-sm">PAX ↓</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.arrival_pax_in}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_pax_in: e.target.value})}
                        className="bg-white dark:bg-[#0f172a]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#64748b] dark:text-gray-400 text-sm">PAX ↑</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.arrival_pax_out}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, arrival_pax_out: e.target.value})}
                        className="bg-white dark:bg-[#0f172a]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Departure (Outbound) Details */}
            {rotationFlights.departure && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-300 dark:border-blue-700">
                <h3 className="font-semibold text-blue-700 dark:text-blue-400 mb-3 flex items-center gap-2">
                  <PlaneTakeoff size={16} />
                  Outbound Flight
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Flight Number</Label>
                    <Input
                      value={aircraftChangeData.departure_flight_number}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_flight_number: e.target.value.toUpperCase()})}
                      placeholder="e.g., FR3534"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Origin</Label>
                    <Input
                      value={aircraftChangeData.departure_origin}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_origin: e.target.value.toUpperCase()})}
                      placeholder="EBCI"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Destination</Label>
                    <Input
                      value={aircraftChangeData.departure_destination}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_destination: e.target.value.toUpperCase()})}
                      placeholder="e.g., GMMX"
                      className="font-mono bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Date</Label>
                    <Input
                      type="date"
                      value={aircraftChangeData.departure_date}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_date: e.target.value})}
                      className="bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#64748b] dark:text-gray-400 text-sm">Time (STD)</Label>
                    <Input
                      type="time"
                      value={aircraftChangeData.departure_time}
                      onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_time: e.target.value})}
                      className="bg-white dark:bg-[#0f172a]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#64748b] dark:text-gray-400 text-sm">PAX ↓</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.departure_pax_in}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_pax_in: e.target.value})}
                        className="bg-white dark:bg-[#0f172a]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#64748b] dark:text-gray-400 text-sm">PAX ↑</Label>
                      <Input
                        type="number"
                        min="0"
                        value={aircraftChangeData.departure_pax_out}
                        onChange={(e) => setAircraftChangeData({...aircraftChangeData, departure_pax_out: e.target.value})}
                        className="bg-white dark:bg-[#0f172a]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setAircraftChangeDialogOpen(false)}
              className="border-[#64748b] text-[#64748b]"
            >
              Cancel
            </Button>
            <Button
              onClick={saveAircraftChange}
              disabled={saving || !aircraftChangeData.registration}
              className="bg-[#d4af37] hover:bg-[#b8972f] text-white"
              data-testid="save-aircraft-change-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Apply Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
